define(function (require, exports, module) {
    'use strict';

    var uid = require('../uid');
    var uploadLog = require('../../uploadLog');
    var UploadLogRecord = require('../../UploadLogRecord');
    var uploadRadar = require('../../uploadRadar');
    var fileSystemPath = require('./fileSystemPath');
    var fileSystemEntryReader = require('./fileSystemEntryReader');
    var AsyncIterator = require('../async/AsyncIterator');
    var AsyncRepeat = require('../async/AsyncRepeat');
    var UploadDescriptor = require('../../UploadDescriptor');
    const { store: reduxStore } = require('reactApp/store');
    const { deferredUpdateUploadFileAction } = require('reactApp/modules/uploadList/uploadList.module');
    const { ActionsBatcher } = require('reactApp/utils/reduxHelpers');
    const { sendGaUploader } = require('reactApp/modules/uploadList/uploadList.helpers');

    const actionsBatcher = new ActionsBatcher((items) => {
        reduxStore.dispatch(deferredUpdateUploadFileAction(items));
    });

    /**
     * @namespace
     */
    var fileSystemEntryHelper = {
        /**
         * @param {FileEntry|DirectoryEntry} entry
         * @returns {UploadDescriptor}
         */
        createDescriptor: function (entry) {
            var radar = uploadRadar.create();
            var localName = fileSystemPath.normalize(entry.name);
            var localPath = fileSystemPath.normalize(entry.fullPath);
            var isDirectory = entry.isDirectory;
            var id = uid();

            var attributes = {
                id: id,
                entry: entry,
                localName: localName,
                localPath: localPath,
                isFile: entry.isFile,
                isDirectory: isDirectory,
                isUnreadDirectory: isDirectory,
                radar: radar,
            };

            if (fileSystemPath.isIgnoredFileName(localName)) {
                sendGaUploader('file', 'ignored_file');
                return null;
            }

            var descriptor = new UploadDescriptor(attributes);
            var kind = isDirectory ? 'folder' : 'file';

            if (!isDirectory) {
                fileSystemEntryReader.getFile(entry).then((file) => {
                    // при dnd размер может придти позже setInputFilesAction
                    actionsBatcher.push({
                        descriptorId: id,
                        size: file.size,
                    });
                    descriptor.set('size', file.size, { silent: true });
                });
            }

            radar.add('kind-' + kind, 0);
            radar.add('state-pending', 0);
            uploadLog.info(
                {
                    id: id,
                    type: 'entry',
                    kind: kind,
                    localPath: localPath,
                },
                UploadLogRecord.LEVEL_IMPORTANT
            );

            return descriptor;
        },

        /**
         * Use in AsyncIterator
         * @param {FileEntry|DirectoryEntry} entry
         * @param {number} index
         * @param {Array.<FileEntry|DirectoryEntry>} files
         * @param {Function} next
         */
        _createDescriptor: function (entry, index, entries, next) {
            // @type {Array.<UploadDescriptor>}
            var descriptors = this;
            // @type {UploadDescriptor}
            var descriptor = fileSystemEntryHelper.createDescriptor(entry);

            if (descriptor) {
                descriptors.push(descriptor);
            }
            next(descriptors);
        },

        /**
         * @param {Array.<FileEntry|DirectoryEntry>} entries
         * @returns {Promise}
         * @resolves {Array.<UploadDescriptor>}
         * @rejects {Error}
         */
        _createDescriptors: function (entries) {
            // @type {Array.<UploadDescriptor>}
            var descriptors = [];
            var promise;

            if (entries.length) {
                promise = new AsyncIterator(entries, fileSystemEntryHelper._createDescriptor, descriptors);
            } else {
                promise = Promise.resolve(descriptors);
            }

            return promise;
        },

        /**
         * @param {UploadDescriptor} descriptor
         * @returns {boolean}
         */
        _isUnread: function (descriptor) {
            var isUnreadDirectory = descriptor.get('isUnreadDirectory');

            return isUnreadDirectory;
        },

        /**
         * @param {UploadDescriptor} descriptor
         */
        _setParent: function (descriptor) {
            // @type {UploadDescriptor}
            var parent = this;

            descriptor.set(
                {
                    parent: parent,
                },
                {
                    silent: true,
                }
            );
        },

        /**
         * @param {UploadDescriptor} descriptor
         * @param {Array.<UploadDescriptor>} descriptors
         */
        _addChildren: function (descriptor, descriptors) {
            descriptors.forEach(fileSystemEntryHelper._setParent, descriptor);
            descriptor.set(
                {
                    isUnreadDirectory: false,
                    children: descriptors, // TODO: UploadDescriptor.List
                },
                {
                    silent: true,
                }
            );
        },

        /**
         * @param {UploadDescriptor} descriptor
         * @param {Error} reason
         */
        _addError: function (descriptor, reason) {
            descriptor.set(
                {
                    isUnreadDirectory: false,
                    error: reason,
                },
                {
                    silent: true,
                }
            );
        },

        /**
         * Use in AsyncIterator
         * @param {UploadDescriptor} descriptor
         * @param {number} index
         * @param {Array.<UploadDescriptor>} descriptors
         * @param {Function} next
         * @param {Function} finish
         * @param {Function} thrower
         */
        _findChildren: function (descriptor, index, descriptors, next, finish, thrower) {
            var result = this;
            var isDirectory = descriptor.get('isDirectory');

            if (isDirectory) {
                result.push(descriptor);

                var isUnreadDirectory = descriptor.get('isUnreadDirectory');

                if (isUnreadDirectory) {
                    var entry = descriptor.get('entry');
                    var promise = fileSystemEntryReader.readDirectory(entry);

                    promise = promise.then(fileSystemEntryHelper._createDescriptors);

                    promise = promise.then(function (descriptors) {
                        // Конкатинируем коллекции в цикле, потому что необходимо
                        // модифицировать исходный массив result, а не создавать новый.
                        for (var i = 0, j = result.length, count = descriptors.length; i < count; i++, j++) {
                            result[j] = descriptors[i];
                        }

                        fileSystemEntryHelper._addChildren(descriptor, descriptors);
                    });

                    promise = promise.catch(function (reason) {
                        fileSystemEntryHelper._addError(descriptor, reason);
                    });

                    promise.then(
                        function () {
                            next();
                        },
                        function (reason) {
                            thrower(reason);
                        }
                    );
                } else {
                    next();
                }
            } else {
                var isFile = descriptor.get('isFile');

                if (isFile) {
                    result.push(descriptor);
                }

                next();
            }
        },

        /**
         * @param {Array.<UploadDescriptor>} descriptors
         * @returns {Promise}
         * @resolves {Array.<UploadDescriptor>}
         * @rejects {Error}
         */
        _flattenHierarchy: function (descriptors) {
            var result = [];
            var promise = new AsyncRepeat(function (repeat, finish, thrower) {
                var promise = new AsyncIterator(descriptors, fileSystemEntryHelper._findChildren, result);

                promise = promise.then(function () {
                    var hasUnreadDirectory = result.some(fileSystemEntryHelper._isUnread);

                    if (hasUnreadDirectory) {
                        // Повторяем чтение папок,
                        // пока не отсанется непрочитанных.
                        descriptors = result;
                        result = [];
                        repeat();
                    } else {
                        finish(result);
                    }
                });

                promise.catch(function (reason) {
                    thrower(reason);
                });
            });

            return promise;
        },

        /**
         * Раскладывает иерархическую структуру в плоский список.
         *                    [
         * 📁docs          →    DirectoryEntry,  docs/
         * ├─📄readme.md   →    FileEntry,       docs/readme.dm
         * ├─📁work        →    DirectoryEntry,  docs/work/
         * │ ├─📁tmp       →    DirectoryEntry,  docs/work/tmp/
         * │ └─📄notes.txt →    FileEntry,       docs/work/notes.txt
         * └─📄error.log   →    FileEntry        docs/error.log
         *                    ]
         * @param {Array.<FileEntry|DirectoryEntry>} entries
         * @returns {Promise}
         * @resolves {Array.<UploadDescriptor>}
         * @rejects {Error}
         */
        getDescriptors: function (entries) {
            var promise = fileSystemEntryHelper._createDescriptors(entries);

            promise = promise.then(fileSystemEntryHelper._flattenHierarchy);

            return promise;
        },
    };

    module.exports = fileSystemEntryHelper;
});
